/**
 * Optimized Multi-Step Charm Builder (with variant selectors & accordion handling)
 * - Fast preview capture (html2canvas -> jpeg blob)
 * - Cloudinary unsigned upload
 * - Sequential add-to-cart (single Preview on chain)
 * - Variant selector population & change handling
 * - Accordion gating for selection
 * - Quantity controls (qty +/-)
 *
 * NOTE: Replace 'YOUR_CLOUD_NAME' and 'YOUR_UPLOAD_PRESET' with your Cloudinary values.
 */

document.addEventListener("DOMContentLoaded", () => {
  /* =========================
     Global State
  ========================= */
  const productCache = new Map();
  let selectedProducts = [];
  let currentStep = 1;
  const placements = new Map();
  let previewBlob = null;
  let isLayoutDirty = true; 
  
  const el = (s) => document.querySelector(s);
  const els = (s) => Array.from(document.querySelectorAll(s));
  const scrollToTop = () => el("#multi-step-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* =========================
     Save Button Helpers
  ========================= */
  
  function setSaveButtonState(saved) {
    const btn = el("#capture-preview");
    if (!btn) return;
    if (saved) {
      btn.disabled = true;
      btn.classList.add("disabled");
      btn.textContent = "Saved";
      isLayoutDirty = false;
    } else {
      btn.disabled = false;
      btn.classList.remove("disabled");
      btn.textContent = "Save";
      isLayoutDirty = true;
    }
  }

  
  const formatPrice = (price) => {
    // keep backwards compatibility for both cents and dollars input
    const val = (typeof price === "number" && price > 1000) ? price / 100 : Number(price);
    return Number(val).toFixed(2);
  };

  const updateTotalPrice = () => {
    const total = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    const totalEl = el("#total-price");
    if (totalEl) totalEl.textContent = `Total: $${total.toFixed(2)}`;
  };

  /* =========================
     Step navigation
  ========================= */
  const setActiveStepDot = (step) => {
    els(".step-dot").forEach((d) => {
      const s = parseInt(d.dataset.step, 10);
      d.classList.toggle("active", s === step);
      d.setAttribute("aria-selected", s === step ? "true" : "false");
    });
  };

  const goToStep = (step) => {
    els(".step").forEach((s) => s.classList.remove("active"));
    const target = el(`.step-${step}`);
    if (target) target.classList.add("active");
    currentStep = step;
    setActiveStepDot(step);

    if (step === 3) {
      updateSelectedProductsUI();
      renderDesigner();
      updateTotalPrice();
    }
    scrollToTop();
  };

  els(".next-step").forEach((b) => b.addEventListener("click", () => {
    if (currentStep < 3) goToStep(currentStep + 1);
  }));
  els(".prev-step").forEach((b) => b.addEventListener("click", () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  }));

  /* =========================================
   Smooth Accordion (Auto-synced animation)
   Prevents product overlap + syncs open/close
  ========================================= */
  els(".accordion-title").forEach((title) => {
    title.addEventListener("click", () => {
      const content = title.nextElementSibling;
      const isActive = title.classList.contains("active");

      // Find transition duration from CSS for timing
      const firstPanel = el(".accordion-content");
      const transitionMs =
        parseFloat(getComputedStyle(firstPanel).transitionDuration) * 1000 || 400;

      // 1️⃣ Close all open panels
      els(".accordion-content").forEach((panel) => {
        if (panel.classList.contains("active")) {
          panel.classList.remove("active");
          panel.setAttribute("aria-hidden", "true");
          panel.style.overflow = "hidden";
          panel.style.maxHeight = panel.scrollHeight + "px";

          // Animate close
          requestAnimationFrame(() => {
            panel.style.maxHeight = "0px";
            panel.style.opacity = "0";
          });

          // Hide only after collapse finishes
          panel.addEventListener(
            "transitionend",
            () => {
              panel.style.display = "none";
              panel.style.padding = "0";
            },
            { once: true }
          );
        }
      });
      els(".accordion-title").forEach((t) => t.classList.remove("active"));

      // 2️⃣ Wait for close animation duration, then open clicked one
      if (!isActive && content) {
        setTimeout(() => {
          // Reset and prepare for expansion
          content.style.display = "flex";
          content.style.padding = "1rem 0";
          content.style.overflow = "hidden";
          content.style.opacity = "1";
          content.style.maxHeight = "none"; // reset before measuring
          const fullHeight = content.scrollHeight;

          // Collapse instantly before expanding
          content.style.maxHeight = "0px";
          void content.offsetHeight; // reflow

          // Animate expansion
          content.style.transition = "max-height 0.4s ease, opacity 0.3s ease";
          content.style.maxHeight = fullHeight + "px";
          content.classList.add("active");
          content.setAttribute("aria-hidden", "false");
          title.classList.add("active");

          // Scroll after animation completes
          const dur =
            parseFloat(getComputedStyle(content).transitionDuration) * 1000 || 400;
          setTimeout(() => {
            const y = title.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: y, behavior: "smooth" });
          }, dur + 50);

          // Cleanup after open animation finishes
          content.addEventListener(
            "transitionend",
            () => {
              content.style.maxHeight = "none";
              content.style.overflow = "visible";
            },
            { once: true }
          );
        }, transitionMs + 50); // 👈 uses the real CSS close duration + buffer
      }
    });
  });


  /* =========================
     Product selection & variant logic
  ========================= */
  const updateNextButtonState = (step) => {
    const btn = el(`.step-${step} .next-step`);
    if (!btn) return;
    const selectedCount = document.querySelectorAll(`.step-${step} .product-card.selected`).length;
    btn.disabled = selectedCount === 0;
  };

  const addOrUpdateSelectedProduct = (payload, qty, step) => {
    const existing = selectedProducts.find((p) => p.handle === payload.handle);
    if (existing) {
      // update values preserving other properties
      existing.title = payload.title;
      existing.image = payload.image;
      existing.price = payload.price;
      existing.variantId = payload.variantId;
      existing.quantity = qty;
      existing.step = step;
    } else {
      selectedProducts.push({ ...payload, quantity: qty, step });
    }
  };

  // Utility: populate variant select on a card given product data
  function populateVariantSelect(card, productData) {
    const select = card.querySelector(".product-variant");
    if (!select) return;
    select.innerHTML = ""; // clear

    if (productData.variants && productData.variants.length > 1) {
      productData.variants.forEach((v) => {
        const opt = document.createElement("option");
        opt.value = String(v.id);
        opt.textContent = v.title;
        select.appendChild(opt);
      });
      select.style.display = "block";
    } else {
      select.style.display = "none";
    }
  }

  // Click handler: selecting/deselecting cards (with accordion gating)
  document.addEventListener("click", async (evt) => {
    const card = evt.target.closest(".product-card");
    if (!card) return;

    // ignore clicks on qty or variant UI to allow their handlers to run
    if (evt.target.closest(".product-qty") || evt.target.closest(".product-variant")) return;

    // If inside accordion, only allow selection when accordion content is active
    const accordionContent = card.closest(".accordion-content");
    if (accordionContent && !accordionContent.classList.contains("active")) return;

    const handle = card.dataset.productHandle;
    const step = Number(card.dataset.step);

    // Step 1 (chain) is single-select
    if (step === 1) {
      els(".step-1 .product-card.selected").forEach((c) => c.classList.remove("selected"));
      selectedProducts = selectedProducts.filter((p) => p.step !== 1);
    }

    // If already selected => deselect
    if (card.classList.contains("selected")) {
      card.classList.remove("selected");
      selectedProducts = selectedProducts.filter((p) => p.handle !== handle);

      // remove all placement keys for this handle (all duplicates)
      Array.from(placements.keys()).forEach((k) => {
        if (k === handle || k.startsWith(handle + "--")) placements.delete(k);
      });

      updateNextButtonState(currentStep);
      if (currentStep === 3) { updateSelectedProductsUI(); renderDesigner(); }
      return;
    }

    // Otherwise, fetch product data (if necessary) and add
    try {
      let productData = productCache.get(handle);
      if (!productData) {
        const res = await fetch(`/products/${handle}.js`);
        productData = await res.json();
        productCache.set(handle, productData);
      }

      // populate variant select on the card (if present)
      populateVariantSelect(card, productData);

      // determine chosen variant (if select exists and visible)
      const variantSelect = card.querySelector(".product-variant");
      let chosenVariant = null;
      if (variantSelect && variantSelect.style.display !== "none") {
        // pick the current select value if set (will be set after populate)
        const selVal = variantSelect.value || variantSelect.querySelector("option")?.value;
        chosenVariant = productData.variants?.find(v => String(v.id) === String(selVal));
      } else {
        chosenVariant = productData.variants?.[0] ?? null;
      }

      const qtyInput = card.querySelector(".qty-input");
      const qty = parseInt(qtyInput?.value, 10) || 1;

      const payload = {
        handle,
        title: productData.title,
        price: (chosenVariant?.price ?? productData.price) / 100,
        image: chosenVariant?.featured_image?.src || productData.featured_image,
        variantId: chosenVariant?.id ?? null,
      };

      addOrUpdateSelectedProduct(payload, qty, step);
      card.classList.add("selected");
      updateNextButtonState(currentStep);

      if (currentStep === 3) { updateSelectedProductsUI(); renderDesigner(); }
    } catch (err) {
      console.error("Error selecting product:", handle, err);
    }
  });

  /* =========================
     Variant change handling (update price/image/variantId in state + UI)
  ========================= */
  document.addEventListener("change", (evt) => {
    if (!evt.target.classList.contains("product-variant")) return;
    const select = evt.target;
    const card = select.closest(".product-card");
    if (!card) return;
    const handle = card.dataset.productHandle;
    const chosenId = select.value;

    const productData = productCache.get(handle);
    if (!productData) return;

    const variant = productData.variants.find(v => String(v.id) === String(chosenId));
    if (!variant) return;

    // Update card price UI
    const priceEl = card.querySelector(".product-price");
    if (priceEl) priceEl.textContent = `$${formatPrice((variant.price ?? productData.price) / 100)}`;

    // Update selectedProducts entry if present
    const item = selectedProducts.find(p => p.handle === handle);
    if (item) {
      item.variantId = variant.id;
      item.price = (variant.price ?? productData.price) / 100;
      item.image = variant?.featured_image?.src || item.image;
      // Refresh step 3 UI if visible
      if (currentStep === 3) updateSelectedProductsUI(), renderDesigner();
    }
  });

  /* =========================
     Quantity controls in product cards (plus/minus)
  ========================= */
  document.addEventListener("click", (evt) => {
    if (!evt.target.matches(".qty-plus") && !evt.target.matches(".qty-minus")) return;
    const card = evt.target.closest(".product-card");
    if (!card) return;
    const input = card.querySelector(".qty-input");
    if (!input) return;
    let val = parseInt(input.value, 10) || 1;
    val = evt.target.matches(".qty-plus") ? val + 1 : Math.max(1, val - 1);
    input.value = val;

    const handle = card.dataset.productHandle;
    const item = selectedProducts.find(p => p.handle === handle);
    if (item) {
      item.quantity = val;
      if (currentStep === 3) updateSelectedProductsUI();
    }
    updateNextButtonState(currentStep);
  });

  /* =========================
     Step 3 Summary UI (selected-products list)
  ========================= */
  const createProductCard = (item) => {
    const div = document.createElement("div");
    div.className = "selected-product";
    let variantTitle = "";
    const cached = productCache.get(item.handle);
    if (cached) {
      const v = cached.variants?.find(v => String(v.id) === String(item.variantId));
      if (v && v.title && v.title.toLowerCase() !== "default title") variantTitle = ` — ${v.title}`;
    }

    div.innerHTML = `
      <button class="remove-product" data-handle="${item.handle}" aria-label="Remove ${item.title}">×</button>
      <img src="${item.image}" alt="${item.title}" />
      <p class="product-title">${item.title}${variantTitle}</p>
      <p class="product-price">$${formatPrice(item.price)}</p>
      <input type="number" value="${item.quantity}" min="1" class="update-qty" data-handle="${item.handle}">
    `;
    return div;
  };

  const updateSelectedProductsUI = () => {
    const list = el("#selected-products-list");
    if (!list) return;
    list.innerHTML = "";
    // show chain first then charms
    const step1 = selectedProducts.filter(p => p.step === 1);
    const step2 = selectedProducts.filter(p => p.step === 2);
    step1.forEach(i => list.appendChild(createProductCard(i)));
    step2.forEach(i => list.appendChild(createProductCard(i)));
    updateTotalPrice();
  };

  document.addEventListener("input", (evt) => {
    if (!evt.target.classList.contains("update-qty")) return;
    const handle = evt.target.dataset.handle;
    const value = Math.max(1, parseInt(evt.target.value, 10) || 1);
    const item = selectedProducts.find(p => p.handle === handle);
    if (!item) return;

    const prevQty = item.quantity || 0;
    item.quantity = value;
    updateTotalPrice();
    setSaveButtonState(false); // layout changed → enable Save again

    const canvas = el("#design-canvas");

    // Remove excess if decreased
    if (value < prevQty) {
      els(`#design-canvas .charm-draggable[data-original-handle="${handle}"]`).forEach((img) => {
        const idx = parseInt((img.dataset.handle || "").split("--")[1], 10);
        if (idx > value) {
          placements.delete(img.dataset.handle);
          img.remove();
        }
      });
      return;
    }

    // Add new charm images if increased
    if (value > prevQty) {
      const diff = value - prevQty;
      const existing = Array.from(placements.entries())
        .filter(([k]) => k.startsWith(handle + "--"))
        .map(([k, pos]) => pos);

      const paddingX = 12;
      const paddingY = 8;
      const totalAfter = selectedProducts
        .filter(p => p.step === 2)
        .reduce((s, p) => s + Math.max(1, p.quantity || 1), 0);
      const perSide = Math.ceil(totalAfter / 2);
      const verticalRange = 100 - 2 * paddingY;
      const verticalStep = verticalRange / (perSide + 1);
      const leftExisting = existing.filter(p => p.side === "left").length;
      const rightExisting = existing.filter(p => p.side === "right").length;
      let leftCount = leftExisting;
      let rightCount = rightExisting;

      for (let i = prevQty; i < value; i++) {
        const uid = `${handle}--${i + 1}`;
        const side = (leftCount <= rightCount) ? "left" : "right";
        const idxOnSide = (side === "left") ? leftCount : rightCount;
        const xPct = side === "left" ? paddingX : 100 - paddingX;
        const yPct = Math.min(95, paddingY + verticalStep * (idxOnSide + 1));
        placements.set(uid, { xPct, yPct, side });
        if (side === "left") leftCount++; else rightCount++;

        const prod = selectedProducts.find(p => p.handle === handle);
        if (prod && canvas) {
          const img = document.createElement("img");
          img.src = prod.image;
          img.alt = prod.title;
          img.className = "charm-draggable";
          if (window.matchMedia("(max-width: 430px)").matches) img.style.width = "100px";
          img.dataset.handle = uid;
          img.dataset.originalHandle = handle;
          img.onload = () => {
            const rect = canvas.getBoundingClientRect();
            const leftPx = (xPct / 100) * rect.width;
            const topPx = (yPct / 100) * rect.height - img.offsetHeight / 2;
            if (side === "left") img.style.left = `${leftPx}px`;
            else img.style.left = `${Math.max(0, leftPx - img.offsetWidth)}px`;
            img.style.top = `${Math.max(0, Math.min(rect.height - img.offsetHeight, topPx))}px`;
          };
          enableDrag(img, canvas);
          canvas.appendChild(img);
        }
      }
    }

    // Render any missing nodes and refresh visuals
    renderDesigner();
  });

  /* =========================
   Delete product from summary
  ========================= */
  document.addEventListener("click", (evt) => {
    if (!evt.target.classList.contains("remove-product")) return;
    const handle = evt.target.dataset.handle;
    // Remove from selectedProducts array
    selectedProducts = selectedProducts.filter(p => p.handle !== handle);

    // Remove all placement keys for this product (handles like handle--1, handle--2, etc)
    Array.from(placements.keys()).forEach((k) => {
      if (k === handle || k.startsWith(handle + "--")) placements.delete(k);
    });

    // Remove charm images from canvas
    els(`#design-canvas .charm-draggable[data-original-handle="${handle}"]`).forEach(img => img.remove());
    // Update UI
    updateSelectedProductsUI();
    updateTotalPrice();
    setSaveButtonState(false);
  });


  /* =========================
     Designer (drag/drop) logic
  ========================= */
  function placeCharm(elm, xPct, yPct, container) {
    const rect = container.getBoundingClientRect();
    elm.style.left = `${(xPct / 100) * rect.width - elm.offsetWidth / 2}px`;
    elm.style.top = `${(yPct / 100) * rect.height - elm.offsetHeight / 2}px`;
  }

  function savePlacement(elm, container) {
    const rect = container.getBoundingClientRect();
    const cx = parseFloat(elm.style.left || "0") + elm.offsetWidth / 2;
    const cy = parseFloat(elm.style.top || "0") + elm.offsetHeight / 2;
    const xPct = Math.min(100, Math.max(0, (cx / rect.width) * 100));
    const yPct = Math.min(100, Math.max(0, (cy / rect.height) * 100));
    // preserve side if previously set
    const prev = placements.get(elm.dataset.handle) || {};
    const side = prev.side || (xPct <= 50 ? "left" : "right");
    placements.set(elm.dataset.handle, { xPct, yPct, side });
  }

  function enableDrag(elm, container) {
    let startX, startY, origLeft, origTop, isDragging = false;

    const startDrag = (e) => {
      e.preventDefault(); // prevent scroll or selection
      isDragging = true;
      const point = e.touches ? e.touches[0] : e;
      startX = point.clientX;
      startY = point.clientY;
      origLeft = parseFloat(elm.style.left || "0");
      origTop = parseFloat(elm.style.top || "0");

      document.addEventListener("pointermove", moveDrag, { passive: false });
      document.addEventListener("pointerup", stopDrag, { once: true });
      document.addEventListener("touchmove", moveDrag, { passive: false });
      document.addEventListener("touchend", stopDrag, { once: true });

      // if layout was previously saved, this marks it “dirty” again
      if (!isLayoutDirty) setSaveButtonState(false);
    };

    const moveDrag = (e) => {
      if (!isDragging) return;
      e.preventDefault(); // stop page scroll during drag
      const point = e.touches ? e.touches[0] : e;
      const dx = point.clientX - startX;
      const dy = point.clientY - startY;

      const left = Math.max(
        0,
        Math.min(container.clientWidth - elm.offsetWidth, origLeft + dx)
      );
      const top = Math.max(
        0,
        Math.min(container.clientHeight - elm.offsetHeight, origTop + dy)
      );

      elm.style.left = `${left}px`;
      elm.style.top = `${top}px`;
    };

    const stopDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      document.removeEventListener("pointermove", moveDrag);
      document.removeEventListener("touchmove", moveDrag);
      savePlacement(elm, container);
    };

    elm.addEventListener("pointerdown", startDrag, { passive: false });
    elm.addEventListener("touchstart", startDrag, { passive: false });
    elm.style.touchAction = "none";
    elm.style.userSelect = "none";
  }


  function renderDesigner() {
    const canvas = el("#design-canvas");
    if (!canvas) return;

    // make sure canvas prevents touch scrolling while user interacts
    canvas.style.touchAction = "none";

    // Set chain background
    const chain = selectedProducts.find((p) => p.step === 1);
    canvas.style.backgroundImage = chain ? `url("${chain.image}")` : "none";

    // Build list of all charm instances required (respect quantity)
    const charms = selectedProducts.filter((p) => p.step === 2);
    const allCharms = [];
    charms.forEach((item) => {
      const qty = Math.max(1, item.quantity || 1);
      for (let i = 0; i < qty; i++) {
        allCharms.push({ ...item, uniqueId: `${item.handle}--${i + 1}` });
      }
    });

    // Map existing DOM nodes by their dataset.handle
    const existingNodes = Array.from(canvas.querySelectorAll(".charm-draggable"));
    const existingMap = new Map(existingNodes.map(n => [n.dataset.handle, n]));

    // Remove any nodes that are no longer needed (product removed or qty decreased)
    const neededIds = new Set(allCharms.map(a => a.uniqueId));
    existingNodes.forEach((node) => {
      if (!neededIds.has(node.dataset.handle)) {
        placements.delete(node.dataset.handle);
        node.remove();
        existingMap.delete(node.dataset.handle);
      }
    });

    // If no charms, exit
    if (allCharms.length === 0) return;

    // Layout defaults (only used for instances that don't already have saved placements)
    let paddingX = 8;
    let paddingY = 8;

    // dynamic chain padding as before (synchronous measurement)
    const canvasRect = canvas.getBoundingClientRect();
    if (chain && chain.image) {
      const img = new Image();
      img.src = chain.image;
      img.onload = () => {
        const chainAspect = img.height / img.width;
        const canvasAspect = canvasRect.height / canvasRect.width || 1;
        const heightRatio = (chainAspect / canvasAspect) * 100;
        paddingY = Math.min(20, Math.max(10, heightRatio * 0.5));
        // After adjusting padding, ensure placements exist for new ones (continue below)
        proceed();
      };
      img.onerror = () => proceed();
    } else {
      proceed();
    }

    function proceed() {
      // compute vertical spacing per side for base positions
      const total = allCharms.length;
      const perSide = Math.ceil(total / 2);
      const verticalRange = 100 - 2 * paddingY;
      const verticalStep = verticalRange / (perSide + 1);

      // generate default placement for each index (only if missing)
      // Left indices are 0,2,4... ; Right are 1,3,5...
      const leftCount = Math.ceil(total / 2);
      const rightCount = Math.floor(total / 2);

      // Keep counters for how many assigned on each side so that when adding new ones we place them in sequence
      const assignedLeft = [];
      const assignedRight = [];

      // First, record existing placements into side-arrays so we can append new ones after them
      Array.from(placements.entries()).forEach(([key, pos]) => {
        if (key.indexOf("--") !== -1) {
          if (pos && pos.side === "right") assignedRight.push({ key, pos });
          else assignedLeft.push({ key, pos });
        }
      });

      // For each required charm instance, create it if missing and set placement if missing
      allCharms.forEach((item, overallIdx) => {
        const uid = item.uniqueId;
        const side = (overallIdx % 2 === 0) ? "left" : "right";

        // If placement exists, leave it (so dragged ones stay put).
        let pos = placements.get(uid);

        if (!pos) {
          // position index along this side: if there are existing (assigned) placements for this side, append after them
          const sideArr = side === "left" ? assignedLeft : assignedRight;
          const idxOnSide = sideArr.length; // place after existing ones
          const xPct = side === "left" ? paddingX : 100 - paddingX;
          const yPct = Math.min(95, paddingY + verticalStep * (idxOnSide + 1));
          pos = { xPct, yPct, side };
          placements.set(uid, pos);
          sideArr.push({ key: uid, pos });
        }

        // If node already exists, ensure it's positioned (do not override if user moved it)
        if (existingMap.has(uid)) {
          // node exists — ensure style is set if not already (first render)
          const node = existingMap.get(uid);
          // if node has no inline left/top (first time), position it
          if (!node.style.left || !node.style.top) {
            // wait for image to load to measure width/height
            if (node.complete) applyPosition(node, pos);
            else node.onload = () => applyPosition(node, pos);
          }
          return;
        }

        // create node (missing)
        const img = document.createElement("img");
        img.src = item.image;
        img.alt = item.title;
        img.className = "charm-draggable";
        img.decoding = "async";
        img.loading = "lazy";
        if (window.matchMedia("(max-width: 430px)").matches) img.style.width = "85px";
        img.dataset.handle = uid;
        img.dataset.originalHandle = item.handle;

        // Position after image loads so offsets are correct
        img.onload = () => {
          applyPosition(img, pos);
        };

        enableDrag(img, canvas);
        canvas.appendChild(img);
        existingMap.set(uid, img);
      });

      // helper: apply pixel-left/top using pos and canvas rect, anchored to side
      function applyPosition(node, pos) {
        const rect = canvas.getBoundingClientRect();
        const leftPx = (pos.xPct / 100) * rect.width;
        const topPx = (pos.yPct / 100) * rect.height - node.offsetHeight / 2;

        if (pos.side === "left") {
          node.style.left = `${leftPx}px`;
        } else {
          node.style.left = `${Math.max(0, leftPx - node.offsetWidth)}px`;
        }
        node.style.top = `${Math.max(0, Math.min(rect.height - node.offsetHeight, topPx))}px`;
      }
    }
  }


  el("#reset-layout")?.addEventListener("click", () => {
    // Clear all saved positions
    placements.clear();
    setSaveButtonState(false);

    // Remove all draggable charms from the canvas
    els("#design-canvas .charm-draggable").forEach((img) => img.remove());

    // Re-render fresh from selection (centers them again)
    renderDesigner();

    // User feedback
    const s = el("#preview-status");
    if (s) {
      s.textContent = "Layout reset";
      setTimeout(() => (s.textContent = ""), 1200);
    }
  });


  /* =========================
     Preview capture (fast)
  ========================= */
  async function preloadImages(container) {
    const imgs = container.querySelectorAll("img");
    await Promise.all([...imgs].map((img) => new Promise((res) => {
      if (img.complete) return res();
      img.onload = res;
      img.onerror = res;
    })));
  }

  el("#capture-preview")?.addEventListener("click", () => {
  const canvas = el("#design-canvas");
  const status = el("#preview-status");
  const btn = el("#capture-preview");
  if (!canvas) return;

  // 1️⃣ Immediate UI feedback
  if (status) status.textContent = "Saving...";
  btn.innerHTML = `<span class="spinner"></span> Saving...`;
  btn.textContent = "Saving...";
  btn.classList.add("disabled");
  btn.disabled = true;

  // Layout is now clean
  isLayoutDirty = false;

  // 2️⃣ Let the UI update first (micro delay)
  setTimeout(async () => {
    try {
      // Ensure all images are ready
      const imgs = canvas.querySelectorAll("img");
      await Promise.all(
        [...imgs].map(
          (img) =>
            new Promise((res) => {
              if (img.complete) res();
              else {
                img.onload = res;
                img.onerror = res;
              }
            })
        )
      );

      // 3️⃣ Faster optimized capture
      const snapshot = await html2canvas(canvas, {
        backgroundColor: null,
        useCORS: true,
        scale: window.innerWidth < 600 ? 0.9 : 1,
        logging: false,
        allowTaint: false,
        imageTimeout: 800,
        removeContainer: true,
      });

      previewBlob = await new Promise((resolve) =>
        snapshot.toBlob(resolve, "image/png")
      );

      // 4️⃣ Success UI
      if (status) status.textContent = "Saved!";
      btn.textContent = "Saved";
      btn.classList.add("disabled");
      btn.disabled = true;

      // Enable Add to Cart now
      el("#add-to-cart").disabled = false;
    } catch (err) {
      console.error("Preview capture failed:", err);
      if (status) status.textContent = "Failed to save";
      btn.textContent = "Save";
      btn.disabled = false;
      btn.classList.remove("disabled");
    }
  }, 50); // 3️⃣ tiny delay → UI updates instantly
});


  /* =========================
     Cloudinary upload
  ========================= */
  async function uploadPreview(blob) {
    const cloudName = 'dbzmquhjm';        
    const uploadPreset = 'unsigned_preview';
    try {
      const fd = new FormData();
      fd.append("file", blob);
      fd.append("upload_preset", uploadPreset);
      fd.append("folder", "charm-previews");

      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, { method: "POST", body: fd });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Cloudinary upload failed: ${res.status} ${txt}`);
      }
      const json = await res.json();
      return json.secure_url || null;
    } catch (err) {
      console.error("[UploadPreview] Cloudinary exception:", err);
      return null;
    }
  }

  /* =========================
     Add to cart (sequential + single Preview on chain)
  ========================= */
  el("#add-to-cart")?.addEventListener("click", async () => {
    const status = el("#preview-status");

    try {
      if (!previewBlob) {
        alert("Please click save first.");
        return;
      }

      if (status) status.textContent = "Uploading preview...";

      // 🔼 Upload preview to Cloudinary only when user is checking out
      const fileUrl = await uploadPreview(previewBlob);
      if (!fileUrl) throw new Error("Preview upload failed");

      // Update status UI
      if (status) status.textContent = "Adding to cart...";

      const chainItem = selectedProducts.find((p) => p.step === 1);
      const charms = selectedProducts.filter((p) => p.step === 2);
      if (!chainItem) {
        alert("Please select a chain first.");
        return;
      }

      // 1️⃣ Add chain with Preview URL
      const chainRes = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: chainItem.variantId,
          quantity: chainItem.quantity,
          properties: {
            Preview: fileUrl,
          },
        }),
      });
      if (!chainRes.ok) throw new Error(`Failed to add chain (${chainRes.status})`);

      // 2️⃣ Add charms sequentially with placement data
      for (const charm of charms) {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: charm.variantId,
            quantity: charm.quantity,
          }),
        });
        if (!res.ok) throw new Error(`Failed to add ${charm.title} (${res.status})`);
      }

      // Redirect to cart page
      window.location.href = "/cart";
    } catch (err) {
      console.error("Add to cart error:", err);
      if (status) status.textContent = "Add to cart failed";
      alert(`Could not add to cart: ${err.message}`);
    }
  });

  /* =========================
     Initialization
  ========================= */
  goToStep(1);
});




// /**
//  * Optimized Multi-Step Charm Builder (with variant selectors & accordion handling)
//  * - Fast preview capture (html2canvas -> jpeg blob)
//  * - Cloudinary unsigned upload
//  * - Sequential add-to-cart (single Preview on chain)
//  * - Variant selector population & change handling
//  * - Accordion gating for selection
//  * - Quantity controls (qty +/-)
//  *
//  * NOTE: Replace 'YOUR_CLOUD_NAME' and 'YOUR_UPLOAD_PRESET' with your Cloudinary values.
//  */

// document.addEventListener("DOMContentLoaded", () => {
//   /* =========================
//      Global State
//   ========================= */
//   const productCache = new Map();
//   let selectedProducts = [];
//   let currentStep = 1;
//   const placements = new Map();
//   let previewBlob = null;

//   /* =========================
//      Helpers
//   ========================= */
//   const el = (s) => document.querySelector(s);
//   const els = (s) => Array.from(document.querySelectorAll(s));
//   const scrollToTop = () => el("#multi-step-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });

//   const formatPrice = (price) => {
//     // keep backwards compatibility for both cents and dollars input
//     const val = (typeof price === "number" && price > 1000) ? price / 100 : Number(price);
//     return Number(val).toFixed(2);
//   };

//   const updateTotalPrice = () => {
//     const total = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
//     const totalEl = el("#total-price");
//     if (totalEl) totalEl.textContent = `Total: $${total.toFixed(2)}`;
//   };

//   /* =========================
//      Step navigation
//   ========================= */
//   const setActiveStepDot = (step) => {
//     els(".step-dot").forEach((d) => {
//       const s = parseInt(d.dataset.step, 10);
//       d.classList.toggle("active", s === step);
//       d.setAttribute("aria-selected", s === step ? "true" : "false");
//     });
//   };

//   const goToStep = (step) => {
//     els(".step").forEach((s) => s.classList.remove("active"));
//     const target = el(`.step-${step}`);
//     if (target) target.classList.add("active");
//     currentStep = step;
//     setActiveStepDot(step);

//     if (step === 3) {
//       updateSelectedProductsUI();
//       renderDesigner();
//       updateTotalPrice();
//     }
//     scrollToTop();
//   };

//   els(".next-step").forEach((b) => b.addEventListener("click", () => {
//     if (currentStep < 3) goToStep(currentStep + 1);
//   }));
//   els(".prev-step").forEach((b) => b.addEventListener("click", () => {
//     if (currentStep > 1) goToStep(currentStep - 1);
//   }));

//   /* =========================================
//    Smooth Accordion (Auto-synced animation)
//    Prevents product overlap + syncs open/close
//   ========================================= */
//   els(".accordion-title").forEach((title) => {
//     title.addEventListener("click", () => {
//       const content = title.nextElementSibling;
//       const isActive = title.classList.contains("active");

//       // Find transition duration from CSS for timing
//       const firstPanel = el(".accordion-content");
//       const transitionMs =
//         parseFloat(getComputedStyle(firstPanel).transitionDuration) * 1000 || 400;

//       // 1️⃣ Close all open panels
//       els(".accordion-content").forEach((panel) => {
//         if (panel.classList.contains("active")) {
//           panel.classList.remove("active");
//           panel.setAttribute("aria-hidden", "true");
//           panel.style.overflow = "hidden";
//           panel.style.maxHeight = panel.scrollHeight + "px";

//           // Animate close
//           requestAnimationFrame(() => {
//             panel.style.maxHeight = "0px";
//             panel.style.opacity = "0";
//           });

//           // Hide only after collapse finishes
//           panel.addEventListener(
//             "transitionend",
//             () => {
//               panel.style.display = "none";
//               panel.style.padding = "0";
//             },
//             { once: true }
//           );
//         }
//       });
//       els(".accordion-title").forEach((t) => t.classList.remove("active"));

//       // 2️⃣ Wait for close animation duration, then open clicked one
//       if (!isActive && content) {
//         setTimeout(() => {
//           // Reset and prepare for expansion
//           content.style.display = "flex";
//           content.style.padding = "1rem 0";
//           content.style.overflow = "hidden";
//           content.style.opacity = "1";
//           content.style.maxHeight = "none"; // reset before measuring
//           const fullHeight = content.scrollHeight;

//           // Collapse instantly before expanding
//           content.style.maxHeight = "0px";
//           void content.offsetHeight; // reflow

//           // Animate expansion
//           content.style.transition = "max-height 0.4s ease, opacity 0.3s ease";
//           content.style.maxHeight = fullHeight + "px";
//           content.classList.add("active");
//           content.setAttribute("aria-hidden", "false");
//           title.classList.add("active");

//           // Scroll after animation completes
//           const dur =
//             parseFloat(getComputedStyle(content).transitionDuration) * 1000 || 400;
//           setTimeout(() => {
//             const y = title.getBoundingClientRect().top + window.scrollY - 100;
//             window.scrollTo({ top: y, behavior: "smooth" });
//           }, dur + 50);

//           // Cleanup after open animation finishes
//           content.addEventListener(
//             "transitionend",
//             () => {
//               content.style.maxHeight = "none";
//               content.style.overflow = "visible";
//             },
//             { once: true }
//           );
//         }, transitionMs + 50); // 👈 uses the real CSS close duration + buffer
//       }
//     });
//   });


//   /* =========================
//      Product selection & variant logic
//   ========================= */
//   const updateNextButtonState = (step) => {
//     const btn = el(`.step-${step} .next-step`);
//     if (!btn) return;
//     const selectedCount = document.querySelectorAll(`.step-${step} .product-card.selected`).length;
//     btn.disabled = selectedCount === 0;
//   };

//   const addOrUpdateSelectedProduct = (payload, qty, step) => {
//     const existing = selectedProducts.find((p) => p.handle === payload.handle);
//     if (existing) {
//       // update values preserving other properties
//       existing.title = payload.title;
//       existing.image = payload.image;
//       existing.price = payload.price;
//       existing.variantId = payload.variantId;
//       existing.quantity = qty;
//       existing.step = step;
//     } else {
//       selectedProducts.push({ ...payload, quantity: qty, step });
//     }
//   };

//   // Utility: populate variant select on a card given product data
//   function populateVariantSelect(card, productData) {
//     const select = card.querySelector(".product-variant");
//     if (!select) return;
//     select.innerHTML = ""; // clear

//     if (productData.variants && productData.variants.length > 1) {
//       productData.variants.forEach((v) => {
//         const opt = document.createElement("option");
//         opt.value = String(v.id);
//         opt.textContent = v.title;
//         select.appendChild(opt);
//       });
//       select.style.display = "block";
//     } else {
//       select.style.display = "none";
//     }
//   }

//   // Click handler: selecting/deselecting cards (with accordion gating)
//   document.addEventListener("click", async (evt) => {
//     const card = evt.target.closest(".product-card");
//     if (!card) return;

//     // ignore clicks on qty or variant UI to allow their handlers to run
//     if (evt.target.closest(".product-qty") || evt.target.closest(".product-variant")) return;

//     // If inside accordion, only allow selection when accordion content is active
//     const accordionContent = card.closest(".accordion-content");
//     if (accordionContent && !accordionContent.classList.contains("active")) return;

//     const handle = card.dataset.productHandle;
//     const step = Number(card.dataset.step);

//     // Step 1 (chain) is single-select
//     if (step === 1) {
//       els(".step-1 .product-card.selected").forEach((c) => c.classList.remove("selected"));
//       selectedProducts = selectedProducts.filter((p) => p.step !== 1);
//     }

//     // If already selected => deselect
//     if (card.classList.contains("selected")) {
//       card.classList.remove("selected");
//       selectedProducts = selectedProducts.filter((p) => p.handle !== handle);
//       placements.delete(handle);
//       updateNextButtonState(currentStep);
//       if (currentStep === 3) { updateSelectedProductsUI(); renderDesigner(); }
//       return;
//     }

//     // Otherwise, fetch product data (if necessary) and add
//     try {
//       let productData = productCache.get(handle);
//       if (!productData) {
//         const res = await fetch(`/products/${handle}.js`);
//         productData = await res.json();
//         productCache.set(handle, productData);
//       }

//       // populate variant select on the card (if present)
//       populateVariantSelect(card, productData);

//       // determine chosen variant (if select exists and visible)
//       const variantSelect = card.querySelector(".product-variant");
//       let chosenVariant = null;
//       if (variantSelect && variantSelect.style.display !== "none") {
//         // pick the current select value if set (will be set after populate)
//         const selVal = variantSelect.value || variantSelect.querySelector("option")?.value;
//         chosenVariant = productData.variants?.find(v => String(v.id) === String(selVal));
//       } else {
//         chosenVariant = productData.variants?.[0] ?? null;
//       }

//       const qtyInput = card.querySelector(".qty-input");
//       const qty = parseInt(qtyInput?.value, 10) || 1;

//       const payload = {
//         handle,
//         title: productData.title,
//         price: (chosenVariant?.price ?? productData.price) / 100,
//         image: chosenVariant?.featured_image?.src || productData.featured_image,
//         variantId: chosenVariant?.id ?? null,
//       };

//       addOrUpdateSelectedProduct(payload, qty, step);
//       card.classList.add("selected");
//       updateNextButtonState(currentStep);

//       if (currentStep === 3) { updateSelectedProductsUI(); renderDesigner(); }
//     } catch (err) {
//       console.error("Error selecting product:", handle, err);
//     }
//   });

//   /* =========================
//      Variant change handling (update price/image/variantId in state + UI)
//   ========================= */
//   document.addEventListener("change", (evt) => {
//     if (!evt.target.classList.contains("product-variant")) return;
//     const select = evt.target;
//     const card = select.closest(".product-card");
//     if (!card) return;
//     const handle = card.dataset.productHandle;
//     const chosenId = select.value;

//     const productData = productCache.get(handle);
//     if (!productData) return;

//     const variant = productData.variants.find(v => String(v.id) === String(chosenId));
//     if (!variant) return;

//     // Update card price UI
//     const priceEl = card.querySelector(".product-price");
//     if (priceEl) priceEl.textContent = `$${formatPrice((variant.price ?? productData.price) / 100)}`;

//     // Update selectedProducts entry if present
//     const item = selectedProducts.find(p => p.handle === handle);
//     if (item) {
//       item.variantId = variant.id;
//       item.price = (variant.price ?? productData.price) / 100;
//       item.image = variant?.featured_image?.src || item.image;
//       // Refresh step 3 UI if visible
//       if (currentStep === 3) updateSelectedProductsUI(), renderDesigner();
//     }
//   });

//   /* =========================
//      Quantity controls in product cards (plus/minus)
//   ========================= */
//   document.addEventListener("click", (evt) => {
//     if (!evt.target.matches(".qty-plus") && !evt.target.matches(".qty-minus")) return;
//     const card = evt.target.closest(".product-card");
//     if (!card) return;
//     const input = card.querySelector(".qty-input");
//     if (!input) return;
//     let val = parseInt(input.value, 10) || 1;
//     val = evt.target.matches(".qty-plus") ? val + 1 : Math.max(1, val - 1);
//     input.value = val;

//     const handle = card.dataset.productHandle;
//     const item = selectedProducts.find(p => p.handle === handle);
//     if (item) {
//       item.quantity = val;
//       if (currentStep === 3) updateSelectedProductsUI();
//     }
//     updateNextButtonState(currentStep);
//   });

//   /* =========================
//      Step 3 Summary UI (selected-products list)
//   ========================= */
//   const createProductCard = (item) => {
//     const div = document.createElement("div");
//     div.className = "selected-product";
//     let variantTitle = "";
//     const cached = productCache.get(item.handle);
//     if (cached) {
//       const v = cached.variants?.find(v => String(v.id) === String(item.variantId));
//       if (v && v.title && v.title.toLowerCase() !== "default title") variantTitle = ` — ${v.title}`;
//     }

//     div.innerHTML = `
//       <button class="remove-product" data-handle="${item.handle}" aria-label="Remove ${item.title}">×</button>
//       <img src="${item.image}" alt="${item.title}" />
//       <p class="product-title">${item.title}${variantTitle}</p>
//       <p class="product-price">$${formatPrice(item.price)}</p>
//       <input type="number" value="${item.quantity}" min="1" class="update-qty" data-handle="${item.handle}">
//     `;
//     return div;
//   };

//   const updateSelectedProductsUI = () => {
//     const list = el("#selected-products-list");
//     if (!list) return;
//     list.innerHTML = "";
//     // show chain first then charms
//     const step1 = selectedProducts.filter(p => p.step === 1);
//     const step2 = selectedProducts.filter(p => p.step === 2);
//     step1.forEach(i => list.appendChild(createProductCard(i)));
//     step2.forEach(i => list.appendChild(createProductCard(i)));
//     updateTotalPrice();
//   };

//   document.addEventListener("input", (evt) => {
//     if (!evt.target.classList.contains("update-qty")) return;
//     const handle = evt.target.dataset.handle;
//     const value = Math.max(1, parseInt(evt.target.value, 10) || 1);
//     const item = selectedProducts.find(p => p.handle === handle);
//     if (!item) return;

//     const prevQty = item.quantity;
//     item.quantity = value;
//     updateTotalPrice();

//     // ✅ Only adjust difference without re-rendering everything
//     if (value > prevQty) {
//       const diff = value - prevQty;
//       for (let i = prevQty; i < prevQty + diff; i++) {
//         const uniqueId = `${handle}--${i + 1}`;
//         if (!placements.has(uniqueId)) {
//           // new charm appears near original handle cluster
//           const basePos = placements.get(`${handle}--1`) || { xPct: 20, yPct: 60 };
//           placements.set(uniqueId, {
//             xPct: Math.min(95, Math.max(5, basePos.xPct + Math.random() * 10 - 5)),
//             yPct: Math.min(95, Math.max(5, basePos.yPct + Math.random() * 10 - 5))
//           });
//         }
//       }
//     } else if (value < prevQty) {
//       // remove extra charms visually and from placements
//       const toRemove = els(`#design-canvas .charm-draggable[data-original-handle="${handle}"]`)
//         .filter(img => {
//           const idx = parseInt(img.dataset.handle.split("--")[1], 10);
//           return idx > value;
//         });
//       toRemove.forEach(img => {
//         placements.delete(img.dataset.handle);
//         img.remove();
//       });
//     }

//     // ✅ Update the canvas without disturbing existing placements
//     renderDesigner();
//   });


//   /* =========================
//    Delete product from summary
//   ========================= */
//   document.addEventListener("click", (evt) => {
//     if (!evt.target.classList.contains("remove-product")) return;
//     const handle = evt.target.dataset.handle;
//     // Remove from selectedProducts array
//     selectedProducts = selectedProducts.filter(p => p.handle !== handle);
//     // Remove placement if it exists
//     placements.delete(handle);
//     // Remove charm from canvas if visible
//     els(`#design-canvas .charm-draggable[data-original-handle="${handle}"]`).forEach(img => img.remove());
//     // Update UI
//     updateSelectedProductsUI();
//     updateTotalPrice();
//   });


//   /* =========================
//      Designer (drag/drop) logic
//   ========================= */
//   function placeCharm(elm, xPct, yPct, container) {
//     const rect = container.getBoundingClientRect();
//     elm.style.left = `${(xPct / 100) * rect.width - elm.offsetWidth / 2}px`;
//     elm.style.top = `${(yPct / 100) * rect.height - elm.offsetHeight / 2}px`;
//   }

//   function savePlacement(elm, container) {
//     const rect = container.getBoundingClientRect();
//     const cx = parseFloat(elm.style.left || "0") + elm.offsetWidth / 2;
//     const cy = parseFloat(elm.style.top || "0") + elm.offsetHeight / 2;
//     const xPct = Math.min(100, Math.max(0, (cx / rect.width) * 100));
//     const yPct = Math.min(100, Math.max(0, (cy / rect.height) * 100));
//     placements.set(elm.dataset.handle, { xPct, yPct });
//   }

//   function enableDrag(elm, container) {
//     let startX, startY, origLeft, origTop;
//     elm.addEventListener("pointerdown", (e) => {
//       e.preventDefault();
//       startX = e.clientX;
//       startY = e.clientY;
//       origLeft = parseFloat(elm.style.left || "0");
//       origTop = parseFloat(elm.style.top || "0");

//       const move = (ev) => {
//         const dx = ev.clientX - startX;
//         const dy = ev.clientY - startY;
//         const left = Math.max(0, Math.min(container.clientWidth - elm.offsetWidth, origLeft + dx));
//         const top = Math.max(0, Math.min(container.clientHeight - elm.offsetHeight, origTop + dy));
//         elm.style.left = `${left}px`;
//         elm.style.top = `${top}px`;
//       };

//       const up = () => {
//         window.removeEventListener("pointermove", move);
//         savePlacement(elm, container);
//       };

//       window.addEventListener("pointermove", move);
//       window.addEventListener("pointerup", up, { once: true });
//     });
//   }

//   function renderDesigner() {
//     const canvas = el("#design-canvas");
//     if (!canvas) return;

//     // Remove existing charms
//     els("#design-canvas .charm-draggable").forEach((n) => n.remove());

//     // Set chain background
//     const chain = selectedProducts.find((p) => p.step === 1);
//     canvas.style.backgroundImage = chain ? `url("${chain.image}")` : "none";

//     const charms = selectedProducts.filter((p) => p.step === 2);
//     const canvasRect = canvas.getBoundingClientRect();

//     // Combine all charms (respecting quantity)
//     const allCharms = [];
//     charms.forEach((item) => {
//       const qty = Math.max(1, item.quantity || 1);
//       for (let i = 0; i < qty; i++) {
//         allCharms.push({ ...item, uniqueId: `${item.handle}--${i + 1}` });
//       }
//     });
//     if (allCharms.length === 0) return;

//     // Default paddings (%)
//     let paddingX = 5;
//     let paddingY = 8;

//     // Detect chain size and adjust vertical padding dynamically
//     function calculateChainPadding(callback) {
//       if (!chain || !chain.image) {
//         callback(paddingY);
//         return;
//       }

//       const img = new Image();
//       img.onload = () => {
//         const chainAspect = img.height / img.width;
//         const canvasAspect = canvasRect.height / canvasRect.width;
//         const heightRatio = (chainAspect / canvasAspect) * 100;
//         const newPadding = Math.min(20, Math.max(10, heightRatio * 0.5));
//         callback(newPadding);
//       };
//       img.onerror = () => callback(paddingY);
//       img.src = chain.image;
//     }

//     calculateChainPadding((dynamicPaddingY) => {
//       paddingY = dynamicPaddingY;

//       const total = allCharms.length;
//       const verticalRange = 100 - 2 * paddingY;
//       const verticalStep = verticalRange / (Math.max(total / 2, 1) + 1);

//       const leftCharms = allCharms.filter((_, i) => i % 2 === 0);
//       const rightCharms = allCharms.filter((_, i) => i % 2 !== 0);

//       // Helper to position each side properly
//       const placeSide = (charmsArr, side) => {
//         charmsArr.forEach((item, idx) => {
//           const img = document.createElement("img");
//           img.src = item.image;
//           img.alt = item.title;
//           img.className = "charm-draggable";
//           img.decoding = "async";
//           img.loading = "lazy";
//           img.dataset.handle = item.uniqueId;
//           img.dataset.originalHandle = item.handle;

//           let pos = placements.get(item.uniqueId);
//           if (!placements.has(item.uniqueId)) {
//             placements.set(item.uniqueId, pos);
//           }

//           // Wait until image loads to get correct width
//           img.onload = () => {
//             const rect = canvas.getBoundingClientRect();
//             const leftPx = (pos.xPct / 100) * rect.width;
//             const topPx = (pos.yPct / 100) * rect.height - img.offsetHeight / 2;

//             // 🔹 Align flush with side padding (not centered)
//             if (pos.side === "left") {
//               img.style.left = `${leftPx}px`;
//             } else {
//               img.style.left = `${leftPx - img.offsetWidth}px`;
//             }
//             img.style.top = `${topPx}px`;
//           };

//           enableDrag(img, canvas);
//           canvas.appendChild(img);
//         });
//       };

//       placeSide(leftCharms, "left");
//       placeSide(rightCharms, "right");
//     });
//   }


//   el("#reset-layout")?.addEventListener("click", () => {
//     // Clear all saved positions
//     placements.clear();

//     // Remove all draggable charms from the canvas
//     els("#design-canvas .charm-draggable").forEach((img) => img.remove());

//     // Re-render fresh from selection (centers them again)
//     renderDesigner();

//     // User feedback
//     const s = el("#preview-status");
//     if (s) {
//       s.textContent = "Layout reset";
//       setTimeout(() => (s.textContent = ""), 1200);
//     }
//   });


//   /* =========================
//      Preview capture (fast)
//   ========================= */
//   async function preloadImages(container) {
//     const imgs = container.querySelectorAll("img");
//     await Promise.all([...imgs].map((img) => new Promise((res) => {
//       if (img.complete) return res();
//       img.onload = res;
//       img.onerror = res;
//     })));
//   }

//   el("#capture-preview")?.addEventListener("click", async () => {
//     const canvas = el("#design-canvas");
//     const status = el("#preview-status");
//     if (!canvas) return;

//     try {
//       if (status) status.textContent = "Saving preview...";

//       // Wait for all charm and chain images to load before capturing
//       const imgs = canvas.querySelectorAll("img");
//       await Promise.all([...imgs].map((img) => new Promise((res) => {
//         if (img.complete) res();
//         else { img.onload = res; img.onerror = res; }
//       })));

//       // Capture the layout as an image (lower scale = faster)
//       const snapshot = await html2canvas(canvas, {
//         backgroundColor: null,
//         useCORS: true,
//         scale: 1.2,
//       });

//       // Convert snapshot to Blob (store locally)
//       previewBlob = await new Promise((resolve) => snapshot.toBlob(resolve, "image/jpeg", 0.85));

//       if (status) {
//         status.textContent = "Preview saved ✔";
//         setTimeout(() => (status.textContent = ""), 1500);
//       }

//       // Enable Add to Cart now that preview exists
//       el("#add-to-cart").disabled = false;
//     } catch (err) {
//       console.error("Preview capture failed:", err);
//       if (status) status.textContent = "Could not capture preview";
//     }
//   });

//   /* =========================
//      Cloudinary upload
//   ========================= */
//   async function uploadPreview(blob) {
//     const cloudName = 'dbzmquhjm';        
//     const uploadPreset = 'unsigned_preview';
//     try {
//       const fd = new FormData();
//       fd.append("file", blob);
//       fd.append("upload_preset", uploadPreset);
//       fd.append("folder", "charm-previews");

//       const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, { method: "POST", body: fd });
//       if (!res.ok) {
//         const txt = await res.text().catch(() => "");
//         throw new Error(`Cloudinary upload failed: ${res.status} ${txt}`);
//       }
//       const json = await res.json();
//       return json.secure_url || null;
//     } catch (err) {
//       console.error("[UploadPreview] Cloudinary exception:", err);
//       return null;
//     }
//   }

//   /* =========================
//      Add to cart (sequential + single Preview on chain)
//   ========================= */
//   el("#add-to-cart")?.addEventListener("click", async () => {
//     const status = el("#preview-status");

//     try {
//       if (!previewBlob) {
//         alert("Please click save first.");
//         return;
//       }

//       if (status) status.textContent = "Uploading preview...";

//       // 🔼 Upload preview to Cloudinary only when user is checking out
//       const fileUrl = await uploadPreview(previewBlob);
//       if (!fileUrl) throw new Error("Preview upload failed");

//       // Update status UI
//       if (status) status.textContent = "Adding to cart...";

//       const chainItem = selectedProducts.find((p) => p.step === 1);
//       const charms = selectedProducts.filter((p) => p.step === 2);
//       if (!chainItem) {
//         alert("Please select a chain first.");
//         return;
//       }

//       // 1️⃣ Add chain with Preview URL
//       const chainRes = await fetch("/cart/add.js", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           id: chainItem.variantId,
//           quantity: chainItem.quantity,
//           properties: {
//             Preview: fileUrl,
//           },
//         }),
//       });
//       if (!chainRes.ok) throw new Error(`Failed to add chain (${chainRes.status})`);

//       // 2️⃣ Add charms sequentially with placement data
//       for (const charm of charms) {
//         const res = await fetch("/cart/add.js", {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({
//             id: charm.variantId,
//             quantity: charm.quantity,
//           }),
//         });
//         if (!res.ok) throw new Error(`Failed to add ${charm.title} (${res.status})`);
//       }

//       // Redirect to cart page
//       window.location.href = "/cart";
//     } catch (err) {
//       console.error("Add to cart error:", err);
//       if (status) status.textContent = "Add to cart failed";
//       alert(`Could not add to cart: ${err.message}`);
//     }
//   });

//   /* =========================
//      Initialization
//   ========================= */
//   goToStep(1);
// });
